import { useEffect, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import { useOverviewContext } from "@features/overview-v2/hooks";
import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";

import { ReorderSavedViews } from "./reorder-saved-views";
import { SavedViewsList } from "./saved-views-list";

interface Props {
  buttonClassName?: string;
  placement?: "bottom" | "bottom-start";
}

export const SavedViewsMenu = ({ buttonClassName, placement }: Props) => {
  const { refetchSavedViews } = useOverviewContext();
  const [reorderingVisible, setReorderingVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isMenuOpen,
    onOpenChange: async (isOpen) => {
      if (isOpen && reorderingVisible) {
        await refetchSavedViews();
      }
      setIsMenuOpen(isOpen);
    },
    transform: false,
    placement: placement ?? "bottom",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(() => {
    if (isMenuOpen) {
      setReorderingVisible(false);
    }
  }, [isMenuOpen]);

  return (
    <div>
      <button
        className={clsx(
          buttonClassName
            ? buttonClassName
            : "shrink-0 rounded-full border border-v2-tertiary h-8 w-8 text-sm font-bold",
          {
            "text-primary hover:bg-cosmic-3": !isMenuOpen,
            "text-white bg-cosmic border-cosmic": isMenuOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <motion.i
          initial={false}
          animate={{ rotate: isMenuOpen ? 0 : 180 }}
          transition={{ duration: 0.2 }}
          className="fa-regular fa-sharp fa-chevron-up font-bold"
        />
      </button>
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={refs.setFloating}
            style={floatingStyles}
            className="absolute z-20 bg-v2-primary rounded-lg elevation-1 text-sm pt-1 pb-2 w-[300px]"
            {...getFloatingProps()}
          >
            {reorderingVisible ? (
              <ReorderSavedViews
                onCancel={() => setReorderingVisible(false)}
                onSelected={() => setIsMenuOpen(false)}
              />
            ) : (
              <SavedViewsList
                isMenuOpen={isMenuOpen}
                onSelected={() => setIsMenuOpen(false)}
                onEnableReordering={() => setReorderingVisible(true)}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
